import { useState } from 'react';
import { useT } from '../hooks/useT';
import { Modal } from './Modal';

export interface CheckoutPromptRequest {
  total: number;
  isFinish: boolean;
  minDarts: number; // minimaal aantal darts waarmee deze uitgooi kan
}

interface CheckoutPromptProps {
  request: CheckoutPromptRequest;
  onConfirm: (opts: { darts: number; dartsAtDouble: number }) => void;
  onCancel: () => void;
}

const choice = (active: boolean) =>
  `min-h-12 min-w-12 flex-1 rounded-2xl text-lg font-bold transition-all duration-150 active:scale-95 disabled:cursor-not-allowed disabled:opacity-30 ${
    active ? 'bg-emerald-700 text-white' : 'bg-black/5 hover:bg-black/10 dark:bg-slate-800/80 dark:hover:bg-slate-700'
  }`;

/**
 * Vraagt bij invoer per beurt om de gegevens die nodig zijn voor een eerlijk gemiddelde en uitgooi-percentage:
 * bij een uitgooi het aantal gebruikte darts en de darts op de dubbel; anders alleen de darts op de dubbel.
 */
export function CheckoutPrompt({ request, onConfirm, onCancel }: CheckoutPromptProps) {
  const { t, tf } = useT();
  const [darts, setDarts] = useState(3);
  const [atDouble, setAtDouble] = useState(1);

  if (!request.isFinish) {
    return (
      <Modal title={t('dartsAtDoubleTitle')} onClose={onCancel}>
        <div className="flex gap-2">
          {[0, 1, 2, 3].map(n => (
            <button key={n} type="button" onClick={() => onConfirm({ darts: 3, dartsAtDouble: n })} className={choice(false)}>
              {n}
            </button>
          ))}
        </div>
      </Modal>
    );
  }

  const confirm = () => onConfirm({ darts, dartsAtDouble: Math.min(atDouble, darts) });

  return (
    <Modal title={tf('checkedOut', { n: request.total })} onClose={onCancel} onConfirm={confirm}>
      <div className="space-y-4">
        <fieldset className="space-y-2">
          <legend className="text-sm text-slate-600 dark:text-slate-400">{t('howManyDarts')}</legend>
          <div className="flex gap-2">
            {[1, 2, 3].map(n => (
              <button
                key={n}
                type="button"
                disabled={n < request.minDarts}
                aria-pressed={darts === n}
                onClick={() => {
                  setDarts(n);
                  setAtDouble(a => Math.min(a, n));
                }}
                className={choice(darts === n)}
              >
                {n}
              </button>
            ))}
          </div>
        </fieldset>
        <fieldset className="space-y-2">
          <legend className="text-sm text-slate-600 dark:text-slate-400">{t('ofWhichDouble')}</legend>
          <div className="flex gap-2">
            {[1, 2, 3].map(n => (
              <button key={n} type="button" disabled={n > darts} aria-pressed={atDouble === n} onClick={() => setAtDouble(n)} className={choice(atDouble === n)}>
                {n}
              </button>
            ))}
          </div>
        </fieldset>
        <button
          type="button"
          onClick={confirm}
          className="min-h-12 w-full rounded-2xl bg-emerald-700 font-bold text-white transition-all duration-150 hover:bg-emerald-800 active:scale-95"
        >
          {t('confirm')}
        </button>
      </div>
    </Modal>
  );
}
